import type { GgxTourDefinition } from './tour-types';

export const HOWTO_08: GgxTourDefinition = {
    key: '[HOWTO-08] Syncing objects from Notebook to GGX',
    route: '/home',
    estimatedMinutes: 6,
    prerequisite: {
        target: '[test-id="profile-details"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Welcome to Corridor Sync',
        subtitle:
            'GGX is not the only place you can build. If you prefer a notebook or an IDE such ' +
            'as VS Code, you can write your prompts and pipelines there as plain Python and ' +
            'sync them straight into the platform, so your workspace and GGX stay in step with ' +
            'no copy-pasting. This tour gets you your API key, shows you where to code, and ' +
            'walks through what a sync script looks like.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have your API key, you know where the notebook lives, and you have seen the ' +
            'three moves of Corridor Sync: connect with ggx.init, declare your prompt and ' +
            'pipeline as Python functions, and call ggx.sync to create or update them in GGX. ' +
            'You have also seen what teams build on top of that: their own evaluation reports ' +
            'as code, human testing pulled into DataFrames, and pipelines exported as runnable ' +
            'Python for debugging and CI. The quickest way to start is to open the Combined ' +
            'Customer Intent and Response Pipeline, choose Export as Python from its More menu, ' +
            'and edit the project it gives you: it is already in the right shape. Everything ' +
            'you sync appears in the registry like anything built in the UI, ready for chats, ' +
            'evaluations and approvals.',
        notebook: 'ggx-sync-from-python.ipynb',
        learned: [
            'Your **API key** connects code to GGX.',
            'Corridor Sync is **init, declare, sync** from Python.',
            'Synced objects appear in the **registry** like anything built in the UI.',
        ],
    },
    chapters: [
        {
            name: 'Get your key',
            blurb: 'The API key that connects your code to GGX',
            icon: 'compass',
            steps: [2, 3],
        },
        {
            name: 'Where to code',
            blurb: 'The notebook that ships with GGX',
            icon: 'cap',
            steps: [4],
        },
        {
            name: 'Sync from Python',
            blurb: 'Connect, declare, sync, and what teams build on it',
            icon: 'pulse',
            steps: [5, 6, 7, 8],
        },
    ],
    steps: [
        {
            target: '[test-id="profile-details"]',
            title: "First, let's get your API key",
            whatHappens: 'your profile menu opens, with the key and the notebook link inside it.',
            icon: 'compass',
            description:
                'Your code needs a way to prove it is you. That is the API key: a personal ' +
                'secret that lets a notebook talk to GGX on your behalf, so everything it ' +
                'creates is tracked under your name. It lives in your profile menu. Click your ' +
                '<strong>avatar</strong> in the top right.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'your avatar',
        },
        {
            target: '[test-id="profile-copy-api-key"]',
            title: 'Copy it, and keep it to yourself',
            whatHappens:
                'the key is copied to your clipboard and the button says Copied for a moment.',
            icon: 'compass',
            description:
                'The <strong>Copy API key</strong> button puts your key on the clipboard. Treat ' +
                'it like a password: paste it into your notebook, never into a shared document ' +
                'or a chat. If it ever leaks, ask an administrator to rotate it. Click ' +
                '<strong>Copy API key</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Copy API key',
        },
        {
            target: '[test-id="profile-menu-item-JupyterHub"]',
            title: 'This is where you can write your code',
            whatHappens:
                'no need to click it now. Continue, and we will look at what a sync script looks ' +
                'like.',
            icon: 'cap',
            description:
                'GGX ships with a <strong>JupyterHub</strong> notebook, one click away from this ' +
                'menu, with the GGX Python package already installed. It is the easiest place to ' +
                'try Corridor Sync. You can also work from VS Code or any Python environment: ' +
                'run <strong>pip install genguardx</strong> and you are set. Leave it for now; ' +
                'the next three cards show you what to write.',
            side: 'left',
            align: 'start',
        },
        {
            title: 'Step one: your notebook becomes a second front door to GGX',
            whatHappens:
                'reading is only half of it. Next, how to write: declaring a prompt as a ' +
                'function so it can be synced.',
            icon: 'pulse',
            description:
                'Everything you can do in the GGX screens, you can also do from a notebook or VS ' +
                'Code, with a few advantages:',
            benefits: [
                'Real Python, your editor',
                'Bulk changes in one run',
                'Reviewed in git like code',
                'Results as DataFrames',
            ],
            tabs: [
                {
                    label: 'Connect',
                    html:
                        '<p data-ggx-lead>Three lines, once per notebook. The address is the one ' +
                        'in your browser; the key is the one you just ' +
                        'copied.</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap># pip install ' +
                        "genguardx        (already installed on GGX's JupyterHub)\n" +
                        'import genguardx as ggx\n' +
                        '\n' +
                        'ggx.init(\n' +
                        '    api_key="paste-the-key-you-just-copied",\n' +
                        '    api_url="https://your-ggx-address",\n' +
                        ')\n' +
                        'ggx.whoami()\n' +
                        "# Logged in as 'your name' to workspace 'your organization'.\n" +
                        '# Any changes made in this session will be tracked under the user ' +
                        '\'you\'.</pre><aside data-ggx-use="Why it matters">Every object you ' +
                        'create or change from here is recorded under your name, so the audit ' +
                        'trail is the same as if you had used the UI.</aside>',
                },
                {
                    label: 'Explore what is there',
                    html:
                        '<p data-ggx-lead>Everything below is about one object, the Combined ' +
                        'Customer Intent and Response Pipeline. Open it, take it apart, and read ' +
                        'the prompt that drives it, without leaving your ' +
                        'notebook.</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap># Open the ' +
                        'pipeline by its alias and check where it stands\n' +
                        'bot = ggx.Pipeline("end_to_end_customer_chat_pipeline")\n' +
                        'print(bot.name, "version", bot.version, "is", bot.current_status)\n' +
                        'bot.description                                    # what it does, in ' +
                        "the team's words\n" +
                        '\n' +
                        '# Take it apart: the two sub-pipelines, their prompts, the model and ' +
                        'the card-data lookup\n' +
                        '[p.name for p in bot.input_pipelines]            # intent pipeline, ' +
                        'response pipeline\n' +
                        '[p.name for p in bot.input_prompts]              # the prompts behind ' +
                        'them\n' +
                        '[m.name for m in bot.input_models]               # the model doing the ' +
                        'thinking\n' +
                        '[r.name for r in bot.input_rags]                 # the lookup that ' +
                        'fetches card data\n' +
                        '\n' +
                        '# Read the intent prompt exactly as it runs today, and what it expects ' +
                        'as input\n' +
                        'intent_prompt = ggx.Prompt("customer_intent_classification_prompt")\n' +
                        'intent_prompt.template\n' +
                        '[arg.alias for arg in intent_prompt.arguments]\n' +
                        '\n' +
                        '# Who reviewed it, what they decided, and when\n' +
                        'for h in bot.approval_histories:\n' +
                        '    print(h.created_date, h.created_by, h.action, h.status, ' +
                        'h.comment)</pre><aside data-ggx-use="Use case">Before you change ' +
                        'anything, this is the five-minute due diligence: what the pipeline is ' +
                        'made of, which prompt does the intent work, and who signed it off. The ' +
                        'same lines, run over every pipeline in a project, give you an inventory ' +
                        'for an audit or a model retirement.</aside>',
                },
                {
                    label: 'Test and evaluate',
                    html:
                        '<p data-ggx-lead>Talk to a pipeline the way a customer would, then read ' +
                        'its evaluation results as data you can chart or ' +
                        'compare.</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap># Ask the ' +
                        'chatbot a question as a specific persona\n' +
                        'bot = ggx.Pipeline("end_to_end_customer_chat_pipeline")\n' +
                        'bot(\n' +
                        '    user_message="What is the balance on my card?",\n' +
                        '    context={"customer_id": "C008", "customer_name": "Henry Adams"},\n' +
                        ')\n' +
                        '\n' +
                        "# Its past evaluations, and one run's results as a DataFrame\n" +
                        "bot.jobs                                            # ['Example " +
                        "Dashboard Run', ...]\n" +
                        'run = bot.get_job(name="Example Dashboard Run")\n' +
                        'run.status\n' +
                        'for ev in run.evaluations:\n' +
                        '    print(ev.name, ev.report.name)\n' +
                        '    df = ev.get_data()                              # scored rows, ' +
                        'ready for pandas</pre><aside data-ggx-use="Use case">Pull the accuracy ' +
                        'report of two runs into pandas and compare them row by row, or schedule ' +
                        'a notebook that re-asks fifty known questions every morning and flags ' +
                        'any answer that changed.</aside>',
                },
            ],
        },
        {
            title: 'Step two: describe an object as a Python function',
            whatHappens:
                'next, the pipeline that uses this prompt, and the one call that syncs it all.',
            icon: 'pulse',
            description:
                'Take the <strong>Combined Customer Intent and Response Pipeline</strong>. Its ' +
                "first move is to classify the customer's intent, and that prompt looks like " +
                'this when written for sync. The decorator holds what you would type into the ' +
                'form; the function holds the rest.',
            tabs: [
                {
                    label: 'The prompt',
                    html:
                        '<pre data-ggx-code="Paste into your notebook" data-ggx-wrap>@ggx.Prompt.declare(\n' +
                        '    name="Customer Intent Classification Prompt",\n' +
                        '    group="Credit Card Pipeline Prompts",\n' +
                        '    project="credit_card_classification",      # the slug in your ' +
                        'address bar\n' +
                        '    task_type="Classification",\n' +
                        '    prompt_type="System Instruction",\n' +
                        ')\n' +
                        'def customer_intent_classification_prompt(\n' +
                        '    user_message: str,\n' +
                        '    *,\n' +
                        '    prompt: str = "You classify BankX customer messages into one of the ' +
                        'predefined card intents ...",\n' +
                        ') -> str:\n' +
                        '    """Maps a customer message to one of the BankX card intents."""\n' +
                        '    return prompt</pre><aside data-ggx-use="Use case">Keep the prompt ' +
                        'in a file next to your tests. Every edit is a diff a colleague can ' +
                        'review before it reaches GGX.</aside>',
                },
                {
                    label: 'What maps where',
                    html:
                        '<ul data-ggx-list><li><strong>Function name</strong> becomes the alias: ' +
                        '<code>customer_intent_classification_prompt</code>. ' +
                        'Syncing again with the same name updates the same ' +
                        'object.</li><li><strong>Docstring</strong> becomes the description on ' +
                        'the Details tab, the first thing a reviewer reads.</li><li>The ' +
                        "<strong>prompt</strong> argument's default is the template. Curly " +
                        'braces such as <code>{user_message}</code> stay ' +
                        'placeholders GGX fills at run time.</li><li>Every other argument ' +
                        'becomes a prompt input, typed from its annotation, with its default ' +
                        'carried over.</li><li><strong>Decorator fields</strong> are the form: ' +
                        'name, group, project, task type, prompt type, prompt elements. Leave ' +
                        'one out and GGX keeps whatever is already ' +
                        'set.</li></ul><aside data-ggx-use="Tip">Not sure how a field is ' +
                        'spelled? Open any object in the UI, choose Export as Python from its ' +
                        'More menu, and read the decorator it gives you.</aside>',
                },
                {
                    label: 'Models, RAGs and more',
                    html:
                        '<p data-ggx-lead>Every kind of GGX object uses the same pattern with ' +
                        'its own decorator. The model the chatbot calls, written for ' +
                        'sync:</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap>@ggx.Model.declare(\n' +
                        '    name="Gemini 2.5 Flash",\n' +
                        '    group="Foundation LLMs",\n' +
                        '    provider="google",\n' +
                        '    model="gemini-2.5-flash",\n' +
                        ')\n' +
                        'def gemini_2_5_flash(text: str, temperature: float = 0, ' +
                        'system_instruction: str = "None") -> dict[str, str]:\n' +
                        '    """Gemini 2.5 Flash from Google: fast and low cost, used for intent ' +
                        'and response steps."""\n' +
                        '    ...                                             # the call to the ' +
                        'provider</pre><ul data-ggx-list><li><code>@ggx.Rag.declare</code> ' +
                        "for the knowledge source that fetches a customer's card " +
                        'data.</li><li><code>@ggx.Report.declare</code> for an ' +
                        'evaluation report, and ' +
                        '<code>@ggx.GlobalFunction.declare</code> for a helper ' +
                        'several pipelines share.</li></ul><aside data-ggx-use="Use case">A ' +
                        'whole use case, its model, RAG, prompts and pipeline, lives in one ' +
                        'reviewed file. Change the project slug and sync the same file to ' +
                        'promote it from a sandbox project to production.</aside>',
                },
            ],
        },
        {
            title: 'Step three: sync the pipeline, and everything it uses',
            whatHappens:
                'GGX prints what it created or updated, and the pipeline shows up in the ' +
                'registry like any other. One more card: what teams build on top of this.',
            icon: 'pulse',
            description:
                'The pipeline is a function too, and its body is the logic you saw on its Code ' +
                "tab: recognise the intent, fetch the customer's card data, write the reply. One " +
                'call sends it all to GGX.',
            tabs: [
                {
                    label: 'The pipeline',
                    html:
                        '<pre data-ggx-code="Paste into your notebook" data-ggx-wrap>@ggx.Pipeline.declare(\n' +
                        '    name="Combined Customer Intent and Response Pipeline",\n' +
                        '    group="Conversational AI Chatbot Pipeline (custom-built)",\n' +
                        '    project="credit_card_classification",\n' +
                        '    pipeline_type="Chat based - OpenAI Spec",\n' +
                        '    usecase_type="Question Answering",\n' +
                        '    task_type="Generative Responses",\n' +
                        ')\n' +
                        'def end_to_end_customer_chat_pipeline(user_message: str, history: list ' +
                        '= (), context: dict | None = None):\n' +
                        '    """Recognises the intent, fetches the customer\'s card data, and ' +
                        'writes the reply."""\n' +
                        '    intent = customer_intent_classification_pipeline(user_message, ' +
                        'history=None, context=None)\n' +
                        '    card_data = fetch_existing_customer_credit_card_details(\n' +
                        '        question=user_message, customer_id=context["customer_id"], ' +
                        'chat_history=str(history)\n' +
                        '    )\n' +
                        '    context.update(**card_data)\n' +
                        '    return existing_customer_credit_card_response(user_message, ' +
                        'str(history), context)\n' +
                        '\n' +
                        'ggx.sync(end_to_end_customer_chat_pipeline)      # shortened from the ' +
                        'real pipeline</pre>',
                },
                {
                    label: 'What sync does',
                    html:
                        '<pre data-ggx-code="Example output" data-ggx-readonly data-ggx-wrap>' +
                        "Attempting to synchronize Pipeline: 'end_to_end_customer_chat_pipeline'\n" +
                        " │ Attempting to synchronize Prompt: 'customer_intent_classification_prompt'\n" +
                        ' │ Successfully updated existing Prompt: Customer Intent Classification Prompt\n' +
                        " ├ [WARN] Skipping syncing of the Model 'gemini_2_5_flash' and its inputs as it is already approved\n" +
                        "Successfully updated existing Pipeline: 'end_to_end_customer_chat_pipeline'" +
                        '</pre>' +
                        '<ul data-ggx-list><li><strong>Create or update by alias.</strong> If ' +
                        '<code>end_to_end_customer_chat_pipeline</code> exists it ' +
                        'is updated in place; otherwise it is created.</li><li><strong>Follows ' +
                        'the calls.</strong> The intent sub-pipeline, the RAG and the response ' +
                        'prompt the body calls are synced along with it, in one ' +
                        'run.</li><li><strong>Respects approvals.</strong> Anything already ' +
                        'approved on the platform, like the model, is reused and never ' +
                        'overwritten. GGX prints what it skipped and why.</li><li><strong>Same ' +
                        'governance.</strong> A synced object lands as a draft in the registry ' +
                        'and goes through the same review and approval as one built in the ' +
                        'UI.</li></ul><aside data-ggx-use="Use cases">Update the wording in ten ' +
                        'prompts and sync them all in one run. Promote a use case from a sandbox ' +
                        'project to production by changing one slug. Keep the whole thing in git ' +
                        'so every change has an author and a reason.</aside>',
                },
                {
                    label: 'Edit, sync, test',
                    html:
                        '<p data-ggx-lead>This loop is where a notebook beats the form: change ' +
                        'the function, sync, and call it, all in one ' +
                        'cell.</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap>ggx.sync(end_to_end_customer_chat_pipeline)\n' +
                        '\n' +
                        'bot = ggx.Pipeline("end_to_end_customer_chat_pipeline")\n' +
                        'bot(\n' +
                        '    user_message="Is international usage enabled on my card?",\n' +
                        '    context={"customer_id": "C001", "customer_name": "Alice Johnson"},\n' +
                        ')\n' +
                        '\n' +
                        '# When you are happy, read the same evaluation the UI shows\n' +
                        'run = bot.get_job(name="Example Dashboard Run")\n' +
                        'run.evaluations[0].get_data().head()</pre><aside data-ggx-use="Shortcut">Open ' +
                        'any pipeline in GGX, choose <strong>Export as Python</strong> from its ' +
                        'More menu, and you get a file in exactly this shape to start ' +
                        'from.</aside>',
                },
            ],
        },
        {
            title: 'Step four: what teams actually build with it',
            whatHappens: 'that is the whole toolkit. The wrap-up shows you where to start.',
            icon: 'compass',
            description:
                'Syncing prompts and pipelines is the everyday use. The same package also lets a ' +
                'risk team write its own evaluation metric, a QA team mine what human testers ' +
                'said, and a platform team run the Combined pipeline anywhere. Three examples, ' +
                'all on the same pipeline.',
            tabs: [
                {
                    label: 'Reports as code',
                    html:
                        '<p data-ggx-lead>A report is a Python function too. This one checks ' +
                        "whether the balance the chatbot quotes matches the customer's real card " +
                        'balance. Sync it and it appears in the report library for everyone, ' +
                        'subject to the same ' +
                        'approvals.</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap>@ggx.Report.declare(\n' +
                        '    name="Balance Answers Match Card Data",\n' +
                        '    object_types=["Pipeline"],\n' +
                        '    project="credit_card_classification",\n' +
                        '    risk_type="Accuracy",\n' +
                        '    task_type="Generative Responses",\n' +
                        '    evaluation_methodology="Rule-based",\n' +
                        '    parameters=[\n' +
                        '        {"alias": "expected_balance", "description": "Column with the ' +
                        'true balance", "is_mandatory": True, "type": "SingleObject"},\n' +
                        '    ],\n' +
                        ')\n' +
                        'def balance_answers_match_card_data(job, data, expected_balance) -> ' +
                        'dict:\n' +
                        '    """Flags replies from the Combined pipeline that quote a balance ' +
                        'different from the card data."""\n' +
                        '    df = data["current"].df()                                   # the ' +
                        "pipeline's replies plus your columns\n" +
                        '    df["quoted"] = ' +
                        'df["output"].str.extract(r"\\$([\\d,]+\\.?\\d*)")[0].str.replace(",", ' +
                        '"")\n' +
                        '    df["is_match"] = df["quoted"].astype(float) == ' +
                        'df[expected_balance].astype(float)\n' +
                        '    return {"data": df, "match_rate": float(df["is_match"].mean())}\n' +
                        '\n' +
                        '\n' +
                        '@ggx.ReportOutput.declare(report="Balance Answers Match Card Data", ' +
                        'name="Match rate", evaluation_methodology="Rule-based")\n' +
                        'def match_rate(job, raw_output) -> float:\n' +
                        '    return raw_output["match_rate"]\n' +
                        '\n' +
                        '\n' +
                        'ggx.sync(balance_answers_match_card_data)          # the outputs ' +
                        'declared above come along</pre><aside data-ggx-use="Use case">Your risk ' +
                        'team has a metric no vendor ships, say a fair-lending check or a ' +
                        'policy-wording rule. Write it once here, sync it, and every pipeline in ' +
                        'the organization can be evaluated against it from the same Run ' +
                        'menu.</aside>',
                },
                {
                    label: 'Human feedback as data',
                    html:
                        '<p data-ggx-lead>Every chat a tester has with the Combined pipeline is ' +
                        'kept: the transcript, each thumbs up or down, the reason, the comment, ' +
                        'the closing notes. Pull it all into a ' +
                        'DataFrame.</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap>import ' +
                        'pandas as pd\n' +
                        '\n' +
                        'bot = ggx.Pipeline("end_to_end_customer_chat_pipeline")\n' +
                        '\n' +
                        'rows = []\n' +
                        'for run in bot.test_runs:                          # one row per rated ' +
                        'reply\n' +
                        '    for turn in run.interactions():\n' +
                        '        if turn.role == "assistant" and turn.rating is not None:\n' +
                        '            rows.append({\n' +
                        '                "session": run.name, "tester": run.created_by, ' +
                        '"persona": run.person_name,\n' +
                        '                "rating": turn.rating, "issue": turn.issue_type, ' +
                        '"comment": turn.comment,\n' +
                        '                "reply": turn.content,\n' +
                        '            })\n' +
                        'feedback = pd.DataFrame(rows)\n' +
                        'feedback[feedback["rating"] ' +
                        '< 0].groupby("issue").size()  # what testers complain about most\n' +
                        '\n' +
                        '# A real session as a ready-made evaluation example, in OpenAI message format\n' +
                        'next(bot.test_runs).openai_messages</pre><aside data-ggx-use="Use case">Turn ' +
                        'a month of human testing into an evaluation dataset: the questions real ' +
                        'testers asked, the replies they rejected, and why. Then run that ' +
                        'dataset against the next version of the prompt before anyone tests it ' +
                        'by hand again.</aside>',
                },
                {
                    label: 'Take it with you',
                    html:
                        '<p data-ggx-lead>Nothing is locked in. On any pipeline, <strong>More, ' +
                        'then Export as Python</strong> downloads a runnable project: a main.py ' +
                        'with a run() entry point, one module per object in its lineage, and a ' +
                        'requirements block. The decorators are left in as comments, so the same ' +
                        'files run anywhere and sync straight ' +
                        'back.</p><pre data-ggx-code="Paste into your notebook" data-ggx-wrap># The same idea ' +
                        'from the notebook: the pipeline as a real Python function in your ' +
                        'kernel\n' +
                        'bot = ggx.Pipeline("end_to_end_customer_chat_pipeline")\n' +
                        'fn = bot.get_python_function()\n' +
                        '\n' +
                        '# Step through it, set breakpoints, write a pytest around it\n' +
                        'reply = fn(user_message="Block my card, it was stolen", history=[], ' +
                        'context={"customer_id": "C001"})\n' +
                        'assert "block" in reply["output"].lower()\n' +
                        '\n' +
                        '# Deploy from code: the same file, a different project slug, run from ' +
                        'CI\n' +
                        'ggx.sync(end_to_end_customer_chat_pipeline)</pre><ul data-ggx-list><li><strong>Local ' +
                        'debugging.</strong> <code>get_python_function()</code> ' +
                        "gives you the pipeline's code as a callable, so you can trace the " +
                        'intent call and the RAG lookup line by line.</li><li><strong>Regression ' +
                        'tests.</strong> Wrap the function in pytest and run your known ' +
                        'questions on every change.</li><li><strong>Promotion and CI.</strong> ' +
                        'Keep the exported project in git; sync from a pipeline job to move a ' +
                        'use case from sandbox to production with one slug ' +
                        'change.</li></ul><aside data-ggx-use="Why this is different">Most tools ' +
                        'are either a UI with no code path or an SDK with no governance. Here ' +
                        'the same object is a form, a Python function and an audit record at ' +
                        'once, and sync still refuses to overwrite anything that is ' +
                        'approved.</aside>',
                },
            ],
        },
    ],
};
